import React, { useCallback } from "react";
import { connect } from "react-redux";
import { goToIndex } from "../../processes/questionProccess";
import { getReplyAnswered, getAnswerForQuestionStem } from "../../selectors/answerSelector";
import { showAnswer, getMock } from "../../selectors/mockSelectors";
import { getFlattenedQuestionsFromMock } from "../../selectors/questionSelectors";
import QuestionPaginationItem from "./QuestionPaginationItem";
import { getMockPreferenceByName } from "../../selectors/mockPreferencesSelectors";

const getFlowAnswersForQuestion = (state, { mockId, questionId }) => {
  return Object.values(state.entities.answers || {}).filter(
    (a) =>
      a?.mockId != null &&
      Number(a.mockId) === Number(mockId) &&
      a?.questionId != null &&
      Number(a.questionId) === Number(questionId) &&
      a?.questionPromptId != null
  );
};

const getPromptById = (state, promptId) => {
  if (promptId == null) return null;
  const questions = Object.values(state.entities.questions || {});
  for (const q of questions) {
    const prompts = q?.meta?.questionPrompts || [];
    const match = prompts.find((p) => Number(p.id) === Number(promptId));
    if (match) return match;
  }
  return null;
};

const isFlowComplete = (state, { mockId, questionId }) => {
  const answers = getFlowAnswersForQuestion(state, { mockId, questionId });
  return answers.some((a) => {
    const prompt = getPromptById(state, a.questionPromptId);
    return prompt?.isFinal === true;
  });
};

const isFlowAllCorrect = (state, { mockId, questionId }) => {
  const answers = getFlowAnswersForQuestion(state, { mockId, questionId });
  if (answers.length === 0) return false;
  return answers.every((a) => a.correct === true);
};

function QuestionPaginationItemContainer(props) {
  const { dispatch, index, isLocked } = props;

  const goToIndexHandler = useCallback(
    () => {
      if (isLocked) return;
      goToIndex(dispatch, index);
    },
    [dispatch, index, isLocked]
  );

  return (
    <QuestionPaginationItem {...props} goToIndexHandler={goToIndexHandler} />
  );
}

function mapStateToProps(state, ownProps) {
  const { question, stem, displayIndex, isFlow, index } = ownProps;
  const questionId = question?.id;
  const mock = getMock(state);
  const mockId = mock?.id;
  const showAnswerPreference = getMockPreferenceByName(
    state,
    "showAnswerStatus"
  );

  let answered = null;
  let isLocked = false;

  if (isFlow) {
    const flowComplete = isFlowComplete(state, { mockId, questionId });
    if (flowComplete) {
      answered = { value: isFlowAllCorrect(state, { mockId, questionId }) };
    }
  } else if (stem) {
    // Block access to stems of interactive questions until the flow is completed (rule #6)
    const questionIsInteractive = question?.format === "interactive";
    if (questionIsInteractive) {
      isLocked = !isFlowComplete(state, { mockId, questionId });
    }

    const answer = getAnswerForQuestionStem(state, {
      questionId: questionId,
      questionStemId: stem.id,
      mockId: mockId,
    });

    if (answer) {
      const allStems = state.entities.questionStems || {};
      const stemObj = allStems[stem.id] || Object.values(allStems).find(
        s => Number(s.id) === Number(stem.id)
      );
      if (stemObj && stemObj.choices) {
        const choice = stemObj.choices.find(
          c => Number(c.id) === Number(answer.choiceId)
        );
        if (choice) {
          answered = { value: choice.correct };
        }
      }
    }
  } else {
    answered = getReplyAnswered(state, {
      questionId: questionId,
      mockId: mockId,
    });
  }

  // Additional lock: any entry that comes after an unfinished flow of the same question.
  if (!isLocked && index != null) {
    const flattened = getFlattenedQuestionsFromMock(state);
    const earlierFlowEntry = flattened.find(
      (e, i) => i < index && e.isFlow && Number(e.question?.id) === Number(questionId)
    );
    if (earlierFlowEntry && !isFlowComplete(state, { mockId, questionId })) {
      isLocked = true;
    }
  }

  return {
    showAnswer: showAnswer(state) && showAnswerPreference,
    currentIndex: state.mock.currentIndex,
    answered,
    displayIndex,
    isLocked,
  };
}

export default connect(mapStateToProps)(QuestionPaginationItemContainer);
